/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Product Offerings Sagas for first step
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.10.0
 */

import { SagaIterator } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';

import {
    loadTableFieldsSuccessful,
    loadTableListAction,
    loadTableListError,
    loadTableListSuccessful,
    loadTableMeta,
    setTableCurrentName,
} from '../../components/Table/actions/tableActions';
import { TABLE_DENSITY_LOAD_SIZE, TABLE_LOAD_SIZE } from '../../components/Table/constants';
import { LOAD_TABLE_META_SUCCESSFUL } from '../../components/Table/constants/actionConstants';
import { makeSelectorTableCurrentName, makeSelectorTableData } from '../../components/Table/selectors/tableSelectors';
import { getIdFromTableName } from '../../components/Table/types/tableMapTypes';
import {
    TableNameType,
    TGetBasketCountSuccessfulAction,
    TLoadTableListAction,
} from '../../components/Table/types/tableTypes';
import { API_HOSTNAME, PRODUCT_OFFERINGS_GET_SUBSCRIBERS, WIZARD, WIZARD_ACTION } from '../../modules/app/Api';
import { EHttpMethod } from '../../modules/app/appConstants';
import { makeSelectTranslationWizardProductOfferings } from '../../modules/internationalization/intSelectors';
import { IWizardProductOfferingsTranslation } from '../../modules/internationalization/types';
import { makeSelectIsDensity } from '../../modules/storage/storageSelectors';
import { EWizardName } from '../../modules/wizard/wizardConstants';
import { makeSelectWizardBasketSnapshotHeader } from '../../modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction } from '../../modules/wizard/wizardTypes';
import { requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import { stringFormat } from '../../utils/stringFormat';
import { getProductSelectSubscribersTableFields } from './components/ProductSelectSubscribersTable/ProductSelectSubscribersTableFields';
import {
    productOfferingsSaveStorageAction,
    productOfferingsSetParamsAction,
    productSubscribersListInitAction,
} from './wizardProductOfferingsActions';
import {
    makeSelectProductOfferingsIsDisconnect,
    makeSelectProductOfferingsIsTariff,
} from './wizardProductOfferingsSelectors';

export function* productSubscribersInitListSaga(): SagaIterator {

    /**
     * Мета (пока только фасетс и фильтры)
     */
    yield put(loadTableMeta({
        tableName: TableNameType.productSelectSubscribers,
        skipLoadList: true,
    }));
    yield take([LOAD_TABLE_META_SUCCESSFUL]);

    /**
     * Поля таблицы (не приходят в мете)
     */
    yield put(setTableCurrentName(TableNameType.productSelectSubscribers));
    const translation: IWizardProductOfferingsTranslation =
        yield select(makeSelectTranslationWizardProductOfferings());
    const isTariff = yield select(makeSelectProductOfferingsIsTariff());
    const isDisconnect = yield select(makeSelectProductOfferingsIsDisconnect());

    yield put(loadTableFieldsSuccessful({
        fields: getProductSelectSubscribersTableFields(isTariff, isDisconnect, translation),
    }));

    /**
     * Запрос данных таблицы
     */
    yield put(loadTableListAction({ force: true }));

}

export function* productSubscribersSetSubsCountSaga(action: TGetBasketCountSuccessfulAction): SagaIterator {
    yield put(productOfferingsSetParamsAction({
        subsCount: action.payload || 0,
    }));
}

export function* productSubscribersGetListSaga(action: TLoadTableListAction): SagaIterator {
    const start = action.payload?.force ?? false;
    const header: string = yield select(makeSelectWizardBasketSnapshotHeader());
    const isDensity: boolean = yield select(makeSelectIsDensity());
    const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const list = yield select(makeSelectorTableData());
    const tableLoadSize = isDensity ? TABLE_DENSITY_LOAD_SIZE : TABLE_LOAD_SIZE;

    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-BASKET-SNAPSHOT': header,
        },
    };

    try {
        const url = API_HOSTNAME + stringFormat(WIZARD, EWizardName.PRODUCT_OFFERINGS)
            + stringFormat(WIZARD_ACTION, PRODUCT_OFFERINGS_GET_SUBSCRIBERS);
        const queryString = formQueryParamsString({
            from: start ? 0 : list.length,
            size: tableLoadSize,
        });
        const tableData = yield call(requestSaga, url + queryString, EHttpMethod.GET, null, options);
        // eslint-disable-next-line
        const data: any[] = tableData?.elements ?? [];
        const dataFormat = data.map((item) => ({
            ...item,
            id: getIdFromTableName(tableName, item),
        }));

        yield put(loadTableListSuccessful({
            tableName: tableName,
            data: [...(start ? [] : list), ...dataFormat],
            count: tableData?.count ?? 0,
            basketAllIn: tableData.basketAllIn,
            tableFooter: undefined,
        }));

        yield put(productOfferingsSetParamsAction({
            subsCount: tableData?.checkedCount ?? 0,
        }));
    } catch (e: any) {
        yield put(loadTableListError({ tableName: tableName, error: e.toString() }));
    }
}

export function* productOfferingsStepSelectSubscribersInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    yield put(productSubscribersListInitAction());
    yield put(productOfferingsSetParamsAction({
        maxSubsForProductActivation: action.payload.step.maxSubsForProductActivation ?? undefined,
        subsCount: action.payload.step.subsCount || 0,
    }));
}

/* Clear Previous wizard steps */
export function* productOfferingsStepSelectSubscribersHandleNextSaga(): SagaIterator {
    yield put(productOfferingsSetParamsAction({
        removedSubscribers: [],
    }));
    yield put(productOfferingsSaveStorageAction());
}
